"use client"

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "../ui/dialog";
import { useCallback, useEffect, useMemo, useState } from "react";
import { deletecompanyentry, deleteremovecompanyrelations } from "@/services/deletion.service";
import { useNavigate, useParams } from "react-router";
import { getCompanyById } from "@/services/companies.service";
import z from "zod";
import { CompanySchema } from "@/schemas/company.schema";
import { DeletionStatus } from "@/constants/status";
import { Trash2 } from "lucide-react";

interface Props {
    updateData: () => void
}

export default function DeleteCompanyModal(props: Props) {
    const [open, setOpen] = useState(false)
    const [deletionStatus, setDeletionStatus] = useState(DeletionStatus.standBy);
    const [failedMessage, setFailedMessage] = useState("");
    const [currentStep, setCurrentStep] = useState(-1);
    const [deletionDetails, setDeletionDetails] = useState<Map<string, string>>();
    const [company, setCompany] = useState<z.infer<typeof CompanySchema> | null>(null);

    const params = useParams();
    const company_id = params?.company_id as string;

    const navigate = useNavigate();

    const steps = useMemo(() => [
        {
            title: "Deleting Relation",
            expectedReturn: "Relation Deleted",
            functionToRun: deleteremovecompanyrelations,
            data: []
        },
        {
            title: "Deleting Entry",
            expectedReturn: "Entry Deleted",
            functionToRun: deletecompanyentry,
            data: []
        },
        {
            title: "Company Removal Completed!",
        },
    ], []);

    const handleSuccess = useCallback(() => {
        setDeletionStatus(DeletionStatus.completed);
        props.updateData()
        navigate(`/companies`);
    }, [props]);

    const formatFormData = useCallback(
        () => {
          const formData = new FormData();
          deletionDetails?.forEach((value, key) => {
            formData.append(key, value);
          });
          return formData;
        },
        [deletionDetails]
    );

    const nextStep = useCallback(() => {
        setCurrentStep((index) => index + 1);
    }, []);

    const startStep = useCallback(async () => {
        if(currentStep < steps.length - 1){
            if(steps[currentStep].functionToRun != undefined){
                const result = await steps[currentStep].functionToRun(
                    formatFormData()
                );
            
                if(result == steps[currentStep].expectedReturn){
                    nextStep();
                } else {
                    console.log(result);
                    setDeletionStatus(DeletionStatus.failed);
                    setFailedMessage("Deletion failed. Check console.log for more information.<br>Otherwise, here: ".concat(result));
                    return;
                }
            } else {
                nextStep();
            }
        } else {
            handleSuccess();
        }
    }, [currentStep, handleSuccess, steps, formatFormData, nextStep]);

    async function handleOnSubmit(){
        setDeletionStatus(DeletionStatus.onGoing);

        const companyDetails = new Map<string, string>();
        companyDetails.set('companyID', company!.ID);
        setDeletionDetails(companyDetails);

        if (currentStep < 0) {
            setCurrentStep((index) => index + 1);
        }

        return;
    }

    const getCompanyDetails = useCallback(async () => {
        const result = await getCompanyById(company_id as string);
        setCompany(() => result);
    }, [company_id]);

    useEffect(() => {
        if (currentStep > -1) {
            startStep();
        }
        getCompanyDetails();
    }, [currentStep, startStep]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer bg-red-600 hover:bg-red-700 active:bg-red-800">Delete Company <Trash2 /></Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogTitle>Delete Company</DialogTitle>
                <DialogDescription>
                    You sure you want to delete: {company?.Name}?
                </DialogDescription>
                <DialogFooter>
                    <div className="grid grid-cols-6 gap-4">
                        <div className="col-start-1 col-end-7 rounded-md border-black">
                            <Button className="cursor-pointer mb-3" onClick={(_e) => handleOnSubmit()}>Delete</Button>
                            {deletionStatus == DeletionStatus.onGoing && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Deleting...</p>
                                </div>
                            )}
                            {deletionStatus == DeletionStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Deletion complete! Will refresh this page momentarily</p>
                                </div>
                            )}
                            {deletionStatus == DeletionStatus.failed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>{failedMessage}</p>
                                </div>
                            )}

                            {currentStep > -1 && (
                                <p>{steps[currentStep].title}</p>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}